import React, { useState, useEffect } from 'react';
import { ComplaintIDBadge } from './Badges';
import { toast } from 'react-toastify';
import GuestPageHeader from './GuestPageHeader';
import Footer from './Footer';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { applyOrgBranding } from '../../utils/orgBranding';
import { formatPhoneNumber, removeCountryCode } from '../../../utils/phoneFormatter';
import { formatRoleName } from '../../../utils/roleFormatter';
import './GuestComplaintConfirmation.css';

function GuestComplaintConfirmation({ complaintId, onLogAnother }) {
  const [displayComplaintId, setDisplayComplaintId] = useState(complaintId);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [organizationName, setOrganizationName] = useState(null);
  const [locationName, setLocationName] = useState(null);
  const [reporterInfo, setReporterInfo] = useState({
    full_name: null,
    email: null,
    contact_number: null,
    role: null
  });
  const [assignedTechnician, setAssignedTechnician] = useState({
    employee_id: null,
    full_name: null,
    email: null,
    contact_number: null,
    role: null
  });
  const [attachedPhotos, setAttachedPhotos] = useState([]);
  const [photoUploadWarning, setPhotoUploadWarning] = useState(null);

  // Fetch organization name and location name on mount
  useEffect(() => {
    const fetchOrganizationAndLocation = async () => {
      try {
        // Get organization_id and location_name from guest_device_data stored in sessionStorage
        // This was stored by DevicePageHandler when validating the QR code token
        const guestDeviceDataStr = sessionStorage.getItem('guest_device_data');
        if (!guestDeviceDataStr) {
          console.warn('No guest_device_data found in sessionStorage');
          return;
        }

        const guestDeviceData = JSON.parse(guestDeviceDataStr);
        const organizationId = guestDeviceData.organization_id;
        const deviceLocationName = guestDeviceData.location_name;

        // Set location name from device data
        if (deviceLocationName) {
          setLocationName(deviceLocationName);
        }

        if (!organizationId) {
          console.warn('No organization_id found in guest device data');
          return;
        }

        const response = await fetch(API_ENDPOINTS.ORGANIZATIONS.GET(parseInt(organizationId, 10)));
        if (response.ok) {
          const data = await response.json();
          if (data && data.name) {
            setOrganizationName(data.name);
          }
          if (data?.branding) {
            applyOrgBranding(data.branding);
          }
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
        // Continue without organization name
      }
    };

    fetchOrganizationAndLocation();
  }, []);

  // Fallback to sessionStorage if complaintId prop is not provided
  useEffect(() => {
    if (!displayComplaintId) {
      const storedComplaintId = sessionStorage.getItem('complaint_id');
      if (storedComplaintId) {
        setDisplayComplaintId(storedComplaintId);
      }
    }
  }, [displayComplaintId]);

  // Retrieve reporter information from sessionStorage
  useEffect(() => {
    const fullName = sessionStorage.getItem('reporter_full_name');
    const email = sessionStorage.getItem('reporter_email');
    const contactNumber = sessionStorage.getItem('reporter_contact_number');
    const role = sessionStorage.getItem('reporter_role');

    if (fullName || email || contactNumber || role) {
      setReporterInfo({
        full_name: fullName,
        email: email,
        contact_number: contactNumber,
        role: role
      });
    }
  }, []);

  // Retrieve assigned technician information from sessionStorage
  useEffect(() => {
    setAssignedTechnician({
      employee_id: sessionStorage.getItem('assigned_employee_id'),
      full_name: sessionStorage.getItem('assigned_employee_name'),
      email: sessionStorage.getItem('assigned_employee_email'),
      contact_number: sessionStorage.getItem('assigned_employee_contact'),
      role: sessionStorage.getItem('assigned_employee_role')
    });
  }, []);

  // Successfully uploaded photo thumbnails (small data URLs) + optional upload warning
  useEffect(() => {
    setPhotoUploadWarning(sessionStorage.getItem('photo_upload_warning'));
    try {
      const raw = sessionStorage.getItem('complaint_photo_previews');
      if (!raw) {
        setAttachedPhotos([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setAttachedPhotos(Array.isArray(parsed) ? parsed : []);
    } catch {
      setAttachedPhotos([]);
    }
  }, []);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(displayComplaintId || '');
      // Show visual feedback
      setCopyFeedback(true);
      toast.success('Complaint ID copied to clipboard!');
      // Reset feedback after 2 seconds
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy ID to clipboard');
    }
  };

  return (
    <div className="guest-page-layout">
      {/* Header */}
      <GuestPageHeader organizationName={organizationName} locationName={locationName} />

      {/* Main Content */}
      <main className="guest-page-main">
        <div className="guest-confirmation-container">
          <div className="confirmation-card">
            {/* Success Section */}
            <div className="success-section">
              <div className="success-icon-animated">✓</div>
              <h1 className="confirmation-title">Complaint Logged!</h1>
              <p className="confirmation-subtitle">
                Our team will investigate and resolve your issue.
              </p>
              {photoUploadWarning && (
                <p className="confirmation-photo-warning">{photoUploadWarning}</p>
              )}
            </div>

            {/* Complaint ID Section */}
            <div className="complaint-id-section">
              <span className="id-label">Complaint ID:</span>
              <div className="id-display-compact">
                <ComplaintIDBadge complaintId={displayComplaintId || 'N/A'} size="large" />
                <button
                  className={`copy-btn-compact ${copyFeedback ? 'copied' : ''}`}
                  onClick={handleCopyId}
                  title="Copy to clipboard"
                  aria-label="Copy complaint ID"
                >
                  {copyFeedback ? '✓' : '📋'}
                </button>
              </div>
              <p className="id-hint">Save this number to track your complaint</p>
            </div>

            {attachedPhotos.length > 0 && (
              <div className="confirmation-photos" aria-label="Attached photos">
                <div className="confirmation-photos-label">
                  {attachedPhotos.length} photo{attachedPhotos.length !== 1 ? 's' : ''} attached
                </div>
                <div className="confirmation-photos-strip">
                  {attachedPhotos.map((photo, index) => (
                    <img
                      key={`${photo.name || 'photo'}-${index}`}
                      src={photo.previewUrl}
                      alt={photo.name || `Attached photo ${index + 1}`}
                      className="confirmation-photo-thumb"
                      title={photo.name || `Photo ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Reported By Info Card */}
            {reporterInfo.full_name && (
              <div className="reported-by-card">
                <div className="card-header">
                  <span className="card-icon">👤</span>
                  <span className="card-title">Reported By</span>
                </div>
                <div className="card-content">
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-text">{reporterInfo.full_name}</span>
                  </div>
                  {reporterInfo.email && (
                    <div className="detail-row">
                      <span className="detail-label">Email:</span>
                      <span className="detail-text">{reporterInfo.email}</span>
                    </div>
                  )}
                  {reporterInfo.contact_number && (
                    <div className="detail-row">
                      <span className="detail-label">Contact:</span>
                      <span className="detail-text">{reporterInfo.contact_number}</span>
                    </div>
                  )}
                  {reporterInfo.role && (
                    <div className="detail-row">
                      <span className="detail-label">Role:</span>
                      <span className="detail-text">{formatRoleName(reporterInfo.role)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assigned Technician Info Card */}
            <div className="reported-by-card assigned-technician-card">
              <div className="card-header">
                <span className="card-icon">⚙️</span>
                <span className="card-title">Assigned Technician</span>
              </div>
              <div className="card-content">
                {assignedTechnician.employee_id ? (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Name:</span>
                      <span className="detail-text detail-text--with-id">
                        {assignedTechnician.full_name || 'Technician'}
                        <span className="employee-id-inline">({assignedTechnician.employee_id})</span>
                      </span>
                    </div>
                    {assignedTechnician.email && (
                      <div className="detail-row">
                        <span className="detail-label">Email:</span>
                        <span className="detail-text">{assignedTechnician.email}</span>
                      </div>
                    )}
                    {assignedTechnician.contact_number && (
                      <div className="detail-row">
                        <span className="detail-label">Contact:</span>
                        <span className="detail-text">{formatPhoneNumber(removeCountryCode(assignedTechnician.contact_number))}</span>
                      </div>
                    )}
                    {assignedTechnician.role && (
                      <div className="detail-row">
                        <span className="detail-label">Role:</span>
                        <span className="detail-text">{formatRoleName(assignedTechnician.role)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className="detail-text">Pending assignment</span>
                  </div>
                )}
              </div>
            </div>

            {/* What Happens Next Info Card */}
            <div className="quick-info">
              <div className="info-header">What Happens Next?</div>
              <div className="info-item">
                <span className="info-emoji">⏱️</span>
                <span>Our team will respond within the <strong>defined TAT</strong> for this location and severity</span>
              </div>
              <div className="info-item">
                <span className="info-emoji">📞</span>
                <span>We may call you for <strong>additional details</strong></span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="confirmation-actions action-palette">
              <button
                type="button"
                className="action-btn action-primary"
                onClick={onLogAnother}
                title="Report another issue for this device"
              >
                <span className="action-icon">📝</span>
                <span className="action-text">Report Another</span>
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default GuestComplaintConfirmation;
